import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MarkdownIt from 'markdown-it';
import { v3Api } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import BlockList from './ChatBlocks/BlockRenderer';
import './chatbot-panel.css';

// Markdown-it config mirrors STORI's chat widget: no raw HTML (safety),
// linkify URLs, treat newlines as <br>, typographer smart quotes/dashes.
// Instantiated once at module scope — instances are stateless and cheap to share.
const md = new MarkdownIt({ html: false, linkify: true, breaks: true, typographer: true });

/**
 * Render markdown to HTML, swallowing any parse errors back to the raw text
 * so a bad input never blanks the message bubble.
 */
function renderMarkdown(text) {
    try {
        return { __html: md.render(String(text || '')) };
    } catch {
        return null;
    }
}

/**
 * Sniff the dominant non-Latin script of a string. Mirrors the server-side
 * languageDetector (same Unicode blocks, same first-match-wins rule) so
 * user messages get the same tag the server would assign — important for
 * mixed-language chats where the UI is English but the user types Arabic.
 */
function detectMsgLanguage(text) {
    const s = String(text || '');
    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (/[ഀ-ൿ]/.test(ch)) return 'ml';
        if (/[ऀ-ॿ]/.test(ch)) return 'hi';
        if (/[؀-ۿ]/.test(ch)) return 'ar';
    }
    return 'en';
}

/**
 * Chatbot Panel — Sprint 2 F3, multilingual update Sprint 3.
 *
 * Floating right-side panel with conversation history + input.
 * Toggled via the Cmd+K / Ctrl+K shortcut or the chat FAB.
 *
 * Each message carries `language` and `dir`. For user messages we detect on
 * the client; for assistant messages we trust the server's `language`/`dir`
 * fields (they come back on the /query response). Arabic bubbles render RTL
 * regardless of the global UI language — important for the common case of
 * an English UI with an Arabic question.
 */
export default function ChatbotPanel({ isOpen, onClose }) {
    const { t } = useTranslation();
    const { propertyAccess } = useAuth();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const abortRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    // Abort any in-flight request on unmount or panel close so we don't
    // setState on an unmounted component and don't leak the user's question.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            abortRef.current?.abort();
        } else {
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    function friendlyError(err) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return null;
        if (err?.code === 'ECONNABORTED') return t('The request timed out. Please try again.');
        if (err?.response?.status === 401 || err?.response?.status === 403) {
            return t('Your session expired. Please sign in again.');
        }
        return t('Sorry, I could not process that. Please try again.');
    }

    async function send(question) {
        if (!question.trim()) return;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const userLang = detectMsgLanguage(question);
        const userDir = userLang === 'ar' ? 'rtl' : 'ltr';
        setMessages(prev => [...prev, { role: 'user', text: question, language: userLang, dir: userDir }]);
        setSending(true);
        try {
            const res = await v3Api.chatbot.query(question, sessionId, propertyAccess, { signal: controller.signal });
            if (!mountedRef.current) return;
            setSessionId(res.data.session_id);
            // Server returns language + dir on the assistant message. Fall back
            // to client-side detection if an older server build hasn't been
            // deployed yet.
            const assistantLang = res.data.language || detectMsgLanguage(res.data.answer_text);
            const assistantDir = res.data.dir || (assistantLang === 'ar' ? 'rtl' : 'ltr');
            setMessages(prev => [...prev, {
                role: 'assistant',
                text: res.data.answer_text,
                data: res.data.data,
                blocks: Array.isArray(res.data.blocks) ? res.data.blocks : [],
                suggestions: res.data.suggested_followups,
                language: assistantLang,
                dir: assistantDir,
            }]);
        } catch (err) {
            console.error('[chatbot] query failed', err);
            const text = friendlyError(err);
            if (!mountedRef.current || text === null) return;
            // Carry the original question so the error bubble can offer a retry.
            setMessages(prev => [...prev, { role: 'assistant', text, isError: true, retryQuestion: question, language: 'en', dir: 'ltr' }]);
        } finally {
            if (mountedRef.current) {
                setSending(false);
                // Defer focus to next tick. The input is disabled={sending}, so
                // at the moment setSending(false) runs the DOM still shows it as
                // disabled and the browser silently drops a same-tick focus().
                setTimeout(() => inputRef.current?.focus(), 0);
            }
        }
    }

    function onSubmit(e) {
        e.preventDefault();
        send(input);
        setInput('');
    }

    if (!isOpen) return null;

    return (
        <div
            className="v3 chat-panel"
            style={{
                position: 'fixed',
                top: 0, right: 0, bottom: 0,
                width: 420,
                display: 'flex', flexDirection: 'column',
                zIndex: 9998,
            }}
        >
            {/* Header */}
            <div className="chat-header">
                <div className="chat-ai-badge">AI</div>
                <div>
                    <div className="v3-title">{t('EGoal Intelligence')}</div>
                    <div className="v3-label" style={{ fontSize: 10, textTransform: 'none' }}>{t('Ask anything about your data')}</div>
                </div>
                <button
                    type="button"
                    className="chat-close"
                    onClick={onClose}
                    aria-label={t('Close chatbot panel')}
                >×</button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="chat-body">
                {messages.length === 0 && (
                    <div className="chat-empty">
                        <div className="chat-empty-icon">
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                            </svg>
                        </div>
                        <div className="chat-empty-title">{t('EGoal Intelligence')}</div>
                        <div className="chat-empty-sub">{t('Ask anything about your data')}</div>
                        <div className="chat-empty-chips">
                            <button type="button" className="chat-chip" onClick={() => send(t('Show top 5 tenants by sales'))}>{t('Show top 5 tenants by sales')}</button>
                            <button type="button" className="chat-chip" onClick={() => send(t('Compare luxury vs electronics this quarter'))}>{t('Compare luxury vs electronics this quarter')}</button>
                        </div>
                    </div>
                )}
                {messages.map((m, i) => (
                    <Msg key={i} msg={m} onSuggest={(s) => send(s)} onRetry={(q) => send(q)} t={t} />
                ))}
                {sending && <TypingIndicator t={t} />}
            </div>

            {/* Input */}
            <form onSubmit={onSubmit} className="chat-input-row">
                <input
                    ref={inputRef}
                    type="text"
                    className="chat-input"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder={t('Ask a question…')}
                    disabled={sending}
                />
                <button type="submit" className="chat-send" disabled={sending || !input.trim()} aria-label={t('Send')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 2 11 13" />
                        <path d="M22 2 15 22l-4-9-9-4 20-7z" />
                    </svg>
                </button>
            </form>
        </div>
    );
}

// Phrase keys cycled while a response is in flight. Each is a translation key
// present in all four locale bundles (en/ar/hi/ml).
const THINKING_PHRASES = ['Thinking…', 'Fetching your data…', 'Crunching the numbers…', 'Putting it together…'];

function TypingIndicator({ t }) {
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setIdx(i => (i + 1) % THINKING_PHRASES.length), 2200);
        return () => clearInterval(id);
    }, []);
    return (
        <div className="chat-typing chat-msg-enter" aria-live="polite">
            <span className="chat-typing-dots"><span /><span /><span /></span>
            <span className="chat-typing-phrase">{t(THINKING_PHRASES[idx])}</span>
        </div>
    );
}

function Msg({ msg, onSuggest, onRetry, t }) {
    const isUser = msg.role === 'user';
    const [copied, setCopied] = useState(false);

    function handleCopy() {
        const text = String(msg.text || '');
        const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500); };
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).then(done).catch(() => {});
        }
    }
    // Per-message dir — Arabic bubbles render RTL regardless of the global
    // UI direction, with text-align flipped to match. Suggestion chips also
    // flip so the chip-row reads in the same direction as the bubble.
    const dir = msg.dir || 'ltr';
    const textAlign = dir === 'rtl' ? 'right' : 'left';
    // Assistant bubbles render through markdown-it; user bubbles + error
    // bubbles stay as plain pre-wrap text. If markdown parsing throws for any
    // reason, fall back to pre-wrap so an answer is never blanked.
    const useMarkdown = !isUser && !msg.isError;
    const mdHtml = useMarkdown ? renderMarkdown(msg.text) : null;
    return (
        <div className="chat-msg-wrap chat-msg-enter" style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '92%', width: isUser ? 'auto' : '92%' }} dir={dir}>
            <div
                dir={dir}
                lang={msg.language || undefined}
                className={[
                    'chat-bubble',
                    isUser ? 'chat-bubble-user' : msg.isError ? 'chat-bubble-error' : 'chat-bubble-bot',
                    useMarkdown && mdHtml ? 'chat-md' : '',
                ].filter(Boolean).join(' ')}
                style={{
                    whiteSpace: useMarkdown && mdHtml ? 'normal' : 'pre-wrap',
                    textAlign,
                }}
                {...(useMarkdown && mdHtml ? { dangerouslySetInnerHTML: mdHtml } : {})}
            >{useMarkdown && mdHtml ? null : msg.text}</div>
            {!isUser && Array.isArray(msg.blocks) && msg.blocks.length > 0 && (
                <BlockList blocks={msg.blocks} dir={dir} onSuggest={onSuggest} />
            )}
            {/* Assistant message actions: copy (normal) / retry (error). */}
            {!isUser && (
                <div className="chat-msg-toolbar" dir={dir}>
                    {msg.isError && msg.retryQuestion ? (
                        <button type="button" className="chat-msg-btn" onClick={() => onRetry(msg.retryQuestion)}>
                            ↻ {t('Retry')}
                        </button>
                    ) : !msg.isError && (
                        <button
                            type="button"
                            className={`chat-msg-btn${copied ? ' copied' : ''}`}
                            onClick={handleCopy}
                            aria-label={t('Copy')}
                        >
                            {copied ? `✓ ${t('Copied')}` : `⧉ ${t('Copy')}`}
                        </button>
                    )}
                </div>
            )}
            {msg.suggestions?.length > 0 && (
                <div
                    dir={dir}
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 6,
                        marginTop: 8,
                        justifyContent: dir === 'rtl' ? 'flex-end' : 'flex-start',
                    }}
                >
                    {msg.suggestions.map((s, i) => (
                        <button key={i} type="button" className="chat-chip" onClick={() => onSuggest(s)} dir={dir}>{s}</button>
                    ))}
                </div>
            )}
        </div>
    );
}
